// Lightweight client-side data layer for Installer side
// Uses localStorage for persistence; if Supabase is configured, you can extend these methods later

const LS_KEYS = {
  JOBS: "rb_jobs",
  BIDS: "rb_bids",
  INSTALLERS: "rb_installers",
  CURRENT_INSTALLER: "rb_current_installer",
};

const uuid = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `rb_${Math.random().toString(36).slice(2)}_${Date.now()}`;

// Helpers to read/write localStorage safely
const readLs = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_e) {
    return fallback;
  }
};
const writeLs = (key, value) =>
  localStorage.setItem(key, JSON.stringify(value));

// Seed demo jobs if empty
export function seedDemoJobsIfEmpty() {
  const jobs = readLs(LS_KEYS.JOBS, []);
  if (jobs.length > 0) return jobs;

  const demo = [
    {
      id: uuid(),
      title: "Replace Asphalt Shingle Roof",
      address: "160 Coventry Green NE, Calgary, AB",
      roofType: "Gable Roof",
      squareFeet: 2034,
      pitchedArea: 1729,
      flatArea: 305,
      detectionMethod: "professional_analysis",
      confidence: "96%",
      status: "open",
      createdAt: new Date().toISOString(),
    },
    {
      id: uuid(),
      title: "Commercial TPO Re-roof",
      address: "5678 E Virginia Beach Blvd, Norfolk, VA 23502",
      roofType: "Flat Commercial",
      squareFeet: 14467,
      pitchedArea: 2170,
      flatArea: 12297,
      detectionMethod: "known_database",
      confidence: "99%",
      status: "open",
      createdAt: new Date().toISOString(),
    },
    {
      id: uuid(),
      title: "Metal Roof Install",
      address: "10349 Courageous Dr, Indianapolis, IN 46236",
      roofType: "Complex Gable",
      squareFeet: 3880,
      pitchedArea: 3500,
      flatArea: 380,
      detectionMethod: "openstreetmap",
      confidence: "95%",
      status: "open",
      createdAt: new Date().toISOString(),
    },
  ];
  writeLs(LS_KEYS.JOBS, demo);
  return demo;
}

export function getJobs() {
  const jobs = readLs(LS_KEYS.JOBS, []);
  return jobs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function getJob(jobId) {
  return getJobs().find((j) => j.id === jobId) || null;
}

export function upsertJob(job) {
  const jobs = getJobs();
  const idx = jobs.findIndex((j) => j.id === job.id);
  if (idx >= 0) {
    jobs[idx] = { ...jobs[idx], ...job, updatedAt: new Date().toISOString() };
  } else {
    jobs.unshift({
      ...job,
      id: job.id || uuid(),
      createdAt: new Date().toISOString(),
      status: job.status || "open",
    });
  }
  writeLs(LS_KEYS.JOBS, jobs);
  return job.id;
}

export function getBidsByJob(jobId) {
  const bids = readLs(LS_KEYS.BIDS, []);
  return bids
    .filter((b) => b.jobId === jobId)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function getBidsByInstaller(installerId) {
  const bids = readLs(LS_KEYS.BIDS, []);
  return bids
    .filter((b) => b.installerId === installerId)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function upsertBid(bid) {
  const bids = readLs(LS_KEYS.BIDS, []);
  const id = bid.id || uuid();
  const now = new Date().toISOString();
  const payload = {
    id,
    jobId: bid.jobId,
    installerId: bid.installerId,
    amountTotal: Number(bid.amountTotal || 0),
    pricePerSqFt: Number(bid.pricePerSqFt || 0),
    materials: bid.materials || "Asphalt Shingles",
    warrantyYears: Number(bid.warrantyYears || 10),
    timelineDays: Number(bid.timelineDays || 5),
    startDateEstimate: bid.startDateEstimate || null,
    notes: bid.notes || "",
    validityUntil: bid.validityUntil || null,
    status: bid.status || "submitted",
    attachments: bid.attachments || [],
    createdAt: bid.createdAt || now,
    updatedAt: now,
  };
  const idx = bids.findIndex((b) => b.id === id);
  if (idx >= 0) bids[idx] = payload;
  else bids.unshift(payload);
  writeLs(LS_KEYS.BIDS, bids);
  return payload;
}

export function getInstallerProfile(installerId) {
  const installers = readLs(LS_KEYS.INSTALLERS, {});
  const existing = installers[installerId];

  const defaultProfile = {
    id: installerId,
    companyName: "Summit Roof Solutions",
    contactName: "Sarah Williams",
    phone: "(555) 987-6543",
    email: "sarah@summitroofs.com",
    address: "City, State",
    serviceAreas: ["Local"],
    rating: 4.8,
    reviews: 89,
    verified: true,
    experienceYears: 8,
    specialties: ["Asphalt Shingles", "Repairs"],
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b332e234?w=80&h=80&fit=crop&crop=face",
    completedJobs: 320,
    baseRateSqFt: 4.25,
    baseTimeline: "4-6 days",
    whatsIncluded: ["Materials & install", "Site cleanup", "Basic permits"],
    bio: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  if (existing) {
    const merged = { ...defaultProfile, ...existing };
    installers[installerId] = merged;
    writeLs(LS_KEYS.INSTALLERS, installers);
    return merged;
  }

  installers[installerId] = defaultProfile;
  writeLs(LS_KEYS.INSTALLERS, installers);
  return defaultProfile;
}

export function saveInstallerProfile(installerId, updates) {
  const installers = readLs(LS_KEYS.INSTALLERS, {});
  const base = installers[installerId] || getInstallerProfile(installerId);
  const next = { ...base, ...updates, updatedAt: new Date().toISOString() };
  installers[installerId] = next;
  writeLs(LS_KEYS.INSTALLERS, installers);
  return next;
}

export function getCurrentInstallerId(user) {
  if (user?.id) return user.id;

  const existing = readLs(LS_KEYS.CURRENT_INSTALLER, null);
  return existing; // return null if not found
}

export function getStats(installerId) {
  const jobs = getJobs();
  const bidsMine = getBidsByInstaller(installerId);
  const awarded = bidsMine.filter((b) => b.status === "awarded").length;
  const pending = bidsMine.filter(
    (b) => b.status === "submitted" || b.status === "shortlisted"
  ).length;
  return {
    jobsOpen: jobs.filter((j) => j.status === "open").length,
    myBids: bidsMine.length,
    awarded,
    pending,
  };
}
